import Phaser from 'phaser';

/**
 * **A estação comercial de um porto: pequena, calma, e inconfundível.**
 *
 * ## O que ela é, e o que não é
 *
 * Uma boia de comércio: um corpo claro, um halo ciano-dourado discreto, uma
 * pulsação lenta, e um símbolo simples de pesca — um anzol, desenhado com
 * traços e não com texto. **Nada de letras no canvas**: um rótulo escrito num
 * `RenderTexture` não se traduz, não se lê num ecrã pequeno e não pertence a uma
 * camada que existe para desenhar o mundo.
 *
 * Ela **não decide nada**. Não sabe o que se vende, não sabe preços, e não sabe
 * se o jogador está perto o suficiente — essa resposta é do servidor e vive no
 * React. O que ela desenha é onde a estação está e até onde vai o alcance, para
 * quem se aproxima ver que chegou.
 *
 * ## Onde ela fica na pilha
 *
 * Abaixo dos cascos: uma estação é um marco, e um marco passa por trás da nave
 * que voa à frente dele. Nunca por cima do HUD, que não é sequer desta camada.
 *
 * ## Pendente de avaliação
 *
 * A aparência **não está aprovada** e nenhuma alegação de desempenho é feita:
 * nada foi medido num navegador. A bancada é do encerramento do projeto.
 */

const TEXTURE = {
  body: 'open-world-merchant-body',
  halo: 'open-world-merchant-halo',
  hook: 'open-world-merchant-hook',
  ring: 'open-world-merchant-ring',
} as const;

const BODY_PX = 96;
const HALO_PX = 160;
const HOOK_PX = 64;
const RING_PX = 256;

const BODY_TINT = 0xf2f7ff;
const HALO_TINT = 0x5fd0ff;
const HOOK_TINT = 0xffd24a;
const RING_TINT = 0x5fd0ff;

/** O que a cena precisa de saber. Posição e alcance, e mais nada. */
export interface MerchantDrawState {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

interface MerchantGraphics {
  fillStyle(color: number, alpha?: number): MerchantGraphics;
  fillCircle(x: number, y: number, radius: number): MerchantGraphics;
  fillRect(x: number, y: number, width: number, height: number): MerchantGraphics;
  lineStyle(width: number, color: number, alpha?: number): MerchantGraphics;
  strokeCircle(x: number, y: number, radius: number): MerchantGraphics;
  beginPath(): MerchantGraphics;
  arc(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    anticlockwise?: boolean,
  ): MerchantGraphics;
  strokePath(): MerchantGraphics;
  lineBetween(x1: number, y1: number, x2: number, y2: number): MerchantGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): MerchantGraphics;
  };
}

/** O corpo: um disco com um colar, para não se ler como um planeta pequeno. */
function makeBodyTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.body)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = BODY_PX / 2;
  graphics.fillStyle(0xffffff, 0.95);
  graphics.fillCircle(centre, centre, centre * 0.52);
  graphics.lineStyle(3, 0xffffff, 0.8);
  graphics.strokeCircle(centre, centre, centre * 0.78);
  /* Quatro braços curtos: uma silhueta técnica, e não uma bola. */
  for (const [dx, dy] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const) {
    graphics.lineStyle(4, 0xffffff, 0.7);
    graphics.lineBetween(
      centre + dx * centre * 0.5,
      centre + dy * centre * 0.5,
      centre + dx * centre * 0.92,
      centre + dy * centre * 0.92,
    );
  }
  graphics.generateTexture(TEXTURE.body, BODY_PX, BODY_PX);
  graphics.destroy();
}

/** O halo. Discreto: dois anéis muito transparentes, e nada de bloom. */
function makeHaloTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.halo)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = HALO_PX / 2;
  for (let step = 6; step >= 1; step -= 1) {
    graphics.fillStyle(0xffffff, 0.035);
    graphics.fillCircle(centre, centre, (centre * step) / 6);
  }
  graphics.generateTexture(TEXTURE.halo, HALO_PX, HALO_PX);
  graphics.destroy();
}

/**
 * **O anzol**: uma haste, uma curva e uma farpa.
 *
 * Um símbolo desenhado, e não uma letra: ele diz "pesca" a quem passa sem
 * depender de idioma nenhum.
 */
function makeHookTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.hook)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = HOOK_PX / 2;
  graphics.lineStyle(5, 0xffffff, 1);
  graphics.lineBetween(centre, HOOK_PX * 0.16, centre, HOOK_PX * 0.56);
  graphics.beginPath();
  graphics.arc(centre, HOOK_PX * 0.6, HOOK_PX * 0.2, 0, Math.PI, false);
  graphics.strokePath();
  graphics.lineBetween(
    centre - HOOK_PX * 0.2,
    HOOK_PX * 0.6,
    centre - HOOK_PX * 0.2,
    HOOK_PX * 0.4,
  );
  graphics.lineBetween(
    centre - HOOK_PX * 0.2,
    HOOK_PX * 0.4,
    centre - HOOK_PX * 0.32,
    HOOK_PX * 0.5,
  );
  graphics.generateTexture(TEXTURE.hook, HOOK_PX, HOOK_PX);
  graphics.destroy();
}

/** O alcance: um anel fino, para se ver que se chegou. */
function makeRingTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.ring)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = RING_PX / 2;
  graphics.lineStyle(2, 0xffffff, 0.8);
  graphics.strokeCircle(centre, centre, centre - 2);
  graphics.generateTexture(TEXTURE.ring, RING_PX, RING_PX);
  graphics.destroy();
}

export class OpenWorldMerchantArt {
  private body: Phaser.GameObjects.Image | null = null;
  private halo: Phaser.GameObjects.Image | null = null;
  private hook: Phaser.GameObjects.Image | null = null;
  private ring: Phaser.GameObjects.Image | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makeBodyTexture(target);
    makeHaloTexture(target);
    makeHookTexture(target);
    makeRingTexture(target);
  }

  /**
   * Um comerciante, ou nenhum.
   *
   * **Nada é criado por quadro**: as quatro imagens nascem na primeira vez que
   * há uma estação e depois só mudam de posição, de escala e de opacidade. Um
   * quadrante sem comerciante esconde-as em vez de as destruir, porque
   * atravessar para um porto e voltar é comum.
   */
  update(
    merchant: MerchantDrawState | null,
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    if (merchant === null) {
      this.setVisible(false);
      return;
    }
    this.build();
    const x = worldUnits(merchant.x);
    const y = worldUnits(merchant.y);
    const reach = worldUnits(merchant.r);
    /* A pulsação: lenta, e a mesma para toda a gente — é uma função do relógio. */
    const pulse = Math.sin(nowMs / 1_400);
    const size = reach * 0.06;

    this.body
      ?.setPosition(x, y)
      .setDisplaySize(size * 2, size * 2)
      .setVisible(true);
    this.halo
      ?.setPosition(x, y)
      .setDisplaySize(size * 5.2, size * 5.2)
      .setAlpha(0.24 + pulse * 0.06)
      .setVisible(true);
    this.hook
      ?.setPosition(x, y)
      .setDisplaySize(size * 1.1, size * 1.1)
      .setAlpha(0.85 + pulse * 0.15)
      .setVisible(true);
    this.ring
      ?.setPosition(x, y)
      .setDisplaySize(reach * 2, reach * 2)
      .setAlpha(0.12 + pulse * 0.04)
      .setVisible(true);
  }

  private setVisible(visible: boolean): void {
    this.body?.setVisible(visible);
    this.halo?.setVisible(visible);
    this.hook?.setVisible(visible);
    this.ring?.setVisible(visible);
  }

  private build(): void {
    if (this.body !== null) {
      return;
    }
    this.ring = this.scene.add
      .image(0, 0, TEXTURE.ring)
      .setDepth(this.depth - 1)
      .setTint(RING_TINT)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.halo = this.scene.add
      .image(0, 0, TEXTURE.halo)
      .setDepth(this.depth)
      .setTint(HALO_TINT)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.body = this.scene.add.image(0, 0, TEXTURE.body).setDepth(this.depth).setTint(BODY_TINT);
    this.hook = this.scene.add
      .image(0, 0, TEXTURE.hook)
      .setDepth(this.depth + 1)
      .setTint(HOOK_TINT);
  }

  destroy(): void {
    this.body?.destroy();
    this.halo?.destroy();
    this.hook?.destroy();
    this.ring?.destroy();
    this.body = null;
    this.halo = null;
    this.hook = null;
    this.ring = null;
  }
}
